/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback } from 'react';
import { Controller, type FieldPath, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ClientSuggestableField } from '@/services/requests/generated/types.gen';
import { fetchClientFieldValues } from '../../services/client-field-values';
import { FieldSuggestInput } from '../FieldSuggest/FieldSuggestInput';
import type { FieldSuggestion, SuggestionFetcher } from '../FieldSuggest/useFieldSuggestions';
import { FieldError } from './FieldError';
import { labelStyle } from './drawer-styles';
import type { ClientFormData } from './schema';

/**
 * The fetcher for one suggestable client field of the agency.
 *
 * @param agencyId - The agency whose clients are read; none means no suggestions.
 * @param field - The suggestable field.
 * @returns A stable fetcher.
 */
export function useClientFieldFetcher(
  agencyId: string | undefined,
  field: ClientSuggestableField,
): SuggestionFetcher {
  return useCallback(async (query: string): Promise<FieldSuggestion[]> => {
    if (!agencyId) return [];
    const response = await fetchClientFieldValues(agencyId, field, query);
    return response.values.map((item) => ({ ...item, context: item.context ?? {} }));
  }, [agencyId, field]);
}

/**
 * The line under a suggestion: "8 clientes · Energia".
 *
 * @param suggestion - The stored value.
 * @param t - Translator scoped to `features/clients`.
 * @returns The count, then whatever the records share.
 */
export function describeClientSuggestion(suggestion: FieldSuggestion, t: TFunction): string {
  const count = t('drawer.suggest.clientCount', { count: suggestion.count });
  return [count, ...Object.values(suggestion.context ?? {})].filter(Boolean).join(' · ');
}

type TextFieldPath = FieldPath<ClientFormData>;

interface ClientSuggestFieldProps {
  form: UseFormReturn<ClientFormData>;
  name: TextFieldPath;
  field: ClientSuggestableField;
  agencyId?: string;
  label: string;
  placeholder?: string;
  maxLength?: number;
  onPick?: (suggestion: FieldSuggestion) => void;
}

/**
 * A free-text client field with the agency's own values as suggestions.
 *
 * @param props - The form, the field path, the suggestable field and copy.
 * @returns The controlled input with its suggestion list.
 */
export function ClientSuggestField({
  form, name, field, agencyId, label, placeholder, maxLength, onPick,
}: ClientSuggestFieldProps) {
  const { t } = useTranslation('features/clients');
  const fetcher = useClientFieldFetcher(agencyId, field);
  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field: input, fieldState }) => (
        <div className="flex flex-col">
          <FieldSuggestInput
            label={label}
            value={typeof input.value === 'string' ? input.value : ''}
            onChange={input.onChange}
            onBlur={input.onBlur}
            onPick={onPick}
            fetcher={fetcher}
            describe={(suggestion) => describeClientSuggestion(suggestion, t)}
            newValueLabel={(typed) => t('drawer.suggest.useAsNew', { value: typed })}
            placeholder={placeholder}
            maxLength={maxLength}
            labelStyle={labelStyle}
            testId={`client-suggest-${field}`}
          />
          <FieldError message={fieldState.error?.message} />
        </div>
      )}
    />
  );
}
